import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import SingleSelectFilterModal from '../../components/modals/SingleSelectFilterModal';
import {
  FileText, Plus, Search, Filter, Edit3, Trash2, Eye, Save, RefreshCw,
  CheckCircle, AlertTriangle, X, Target, Activity, Users, MessageSquare,
  Calendar, Mail, Database, Server, Shield, Bell, Download, Upload,
  Settings, TestTube, BarChart3, TrendingUp, PieChart, Globe, Lock,
  Copy, ExternalLink, Clock, Zap, Star, Heart, Coffee, Pizza, Cake, Music,
  Camera, Video, File, Share, Bookmark, Flag, Monitor, Cpu, HardDrive,
  Network, Wifi, Signal, Battery, Volume, Volume2, VolumeX, Play,
  Pause, Stop, SkipBack, SkipForward, RotateCcw, RotateCw, Maximize,
  Minimize, Maximize2, Minimize2, Square, Circle, Triangle, Hexagon,
  Octagon, Pentagon, Diamond, Star as StarIcon, PieChart as PieChartIcon,
  ChevronDown, Check
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showCategoryFilterModal, setShowCategoryFilterModal] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getEmailTemplates();
        if (response.success) {
          setTemplates(response.data);
        } else {
          setError('Failed to load email templates');
        }
      } catch (err) {
        setError('Error loading email templates');
        console.error('Email templates error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowForm(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowForm(true);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
  };

  const handleDuplicateTemplate = (template) => {
    const duplicatedTemplate = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (Copy)`,
      createdAt: new Date().toISOString()
    };
    setTemplates([...templates, duplicatedTemplate]);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        const updatedTemplates = templates.filter(template => template.id !== templateId);
        setTemplates(updatedTemplates);
        //console.log(`Deleted template ${templateId}`);
      } catch (err) {
        console.error('Failed to delete template:', err);
      }
    }
  };

  const handleTestTemplate = async (template) => {
    try {
      // Simulate template testing with sample data
      const sampleData = {
        ticket_number: 'TKT-001',
        customer_name: 'John Doe',
        agent_name: 'Jane Smith',
        department: 'Support',
        priority: 'High',
        status: 'Open',
        created_at: new Date().toLocaleDateString(),
        subject: 'Test Email Subject'
      };

      // Replace variables in template
      let processedContent = template.content;
      Object.keys(sampleData).forEach(key => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedContent = processedContent.replace(regex, sampleData[key]);
      });

      alert(`Test email sent successfully!\n\nProcessed content:\n${processedContent}`);
    } catch (err) {
      console.error('Template test failed:', err);
      alert('Template test failed. Please check your template.');
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    switch (category) {
      case 'welcome': return 'bg-green-100 text-green-800';
      case 'escalation': return 'bg-red-100 text-red-800';
      case 'resolution': return 'bg-blue-100 text-blue-800';
      case 'notification': return 'bg-yellow-100 text-yellow-800';
      case 'follow-up': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'welcome', label: 'Welcome' },
    { value: 'escalation', label: 'Escalation' },
    { value: 'resolution', label: 'Resolution' },
    { value: 'notification', label: 'Notification' },
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'system', label: 'System' }
  ];

  const getCategoryLabel = (value) => {
    const option = categoryOptions.find(opt => opt.value === value);
    return option ? option.label : 'All Categories';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading email templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600">{error}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Email Templates</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create and manage email templates for automated communications
          </p>
        </div>
        <Button onClick={handleCreateTemplate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Template
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setShowCategoryFilterModal(true)}
          className="w-48 h-10 justify-between"
        >
          <span className="text-sm">
            {filterCategory === 'all' ? 'Filter by category' : getCategoryLabel(filterCategory)}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </Button>
      </div>

      {/* Templates List */}
      <div className="grid gap-4">
        {filteredTemplates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {template.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {template.subject}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={getCategoryColor(template.category)}>
                        {template.category}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        Created: {new Date(template.createdAt).toLocaleDateString()}
                      </span>
                      {template.isActive && (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreviewTemplate(template)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestTemplate(template)}
                  >
                    <TestTube className="h-4 w-4 mr-1" />
                    Test
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDuplicateTemplate(template)}
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Duplicate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditTemplate(template)}
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Template Preview */}
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template Content Preview
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 max-h-20 overflow-hidden">
                  {template.content.substring(0, 200)}...
                </div>
              </div>

              {/* Variables Used */}
              {template.variables && template.variables.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Available Variables
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {template.variables.map((variable, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {`{{${variable}}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No templates found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery || filterCategory !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first email template.'
            }
          </p>
          {(!searchQuery && filterCategory === 'all') && (
            <Button onClick={handleCreateTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          )}
        </div>
      )}

      {/* Template Form Modal */}
      {showForm && (
        <TemplateForm
          template={selectedTemplate}
          onClose={() => setShowForm(false)}
          onSave={(templateData) => {
            if (selectedTemplate) {
              // Update existing template
              const updatedTemplates = templates.map(template =>
                template.id === selectedTemplate.id ? { ...template, ...templateData } : template
              );
              setTemplates(updatedTemplates);
            } else {
              // Add new template
              const newTemplate = {
                id: Date.now().toString(),
                ...templateData,
                isActive: true,
                createdAt: new Date().toISOString()
              };
              setTemplates([...templates, newTemplate]);
            }
            setShowForm(false);
          }}
        />
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <TemplatePreview
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}

      {/* Category Filter Modal */}
      <SingleSelectFilterModal
        isOpen={showCategoryFilterModal}
        onClose={() => setShowCategoryFilterModal(false)}
        onSelect={(value) => setFilterCategory(value)}
        selectedValue={filterCategory}
        options={categoryOptions}
        title="Filter by Category"
        placeholder="Select category"
        searchPlaceholder="Search categories..."
      />
    </div>
  );
};

// Template Form Component
const TemplateForm = ({ template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    subject: template?.subject || '',
    category: template?.category || 'notification',
    content: template?.content || '',
    variables: template?.variables || [],
    isActive: template?.isActive || true
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const categoryOptions = [
    { value: 'welcome', label: 'Welcome' },
    { value: 'escalation', label: 'Escalation' },
    { value: 'resolution', label: 'Resolution' },
    { value: 'notification', label: 'Notification' },
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'system', label: 'System' }
  ];

  const getCategoryLabel = (value) => {
    const option = categoryOptions.find(opt => opt.value === value);
    return option ? option.label : 'Notification';
  };

  const [availableVariables] = useState([
    'ticket_number', 'customer_name', 'agent_name', 'department', 
    'priority', 'status', 'created_at', 'updated_at', 'due_date',
    'subject', 'description', 'resolution', 'company_name'
  ]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddVariable = (variable) => {
    if (!formData.variables.includes(variable)) {
      setFormData(prev => ({
        ...prev,
        variables: [...prev.variables, variable]
      }));
    }
  };

  const handleRemoveVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter(v => v !== variable)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {template ? 'Edit Email Template' : 'Create Email Template'}
            </h2>
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Welcome Email"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category *
                </label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCategoryModal(true)}
                  className="w-full h-10 justify-between"
                >
                  <span>{getCategoryLabel(formData.category)}</span>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </Button>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Subject *
              </label>
              <Input
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                placeholder="e.g., Your ticket {{ticket_number}} has been created"
                required
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Content *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                placeholder="Enter your email template content here..."
                className="w-full h-64 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                required
              />
            </div>

            {/* Variables */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Available Variables
              </label>
              <div className="grid grid-cols-3 gap-2">
                {availableVariables.map((variable) => (
                  <Button
                    key={variable}
                    type="button"
                    variant={formData.variables.includes(variable) ? "default" : "outline"}
                    size="sm"
                    onClick={() => 
                      formData.variables.includes(variable) 
                        ? handleRemoveVariable(variable)
                        : handleAddVariable(variable)
                    }
                    className="justify-start"
                  >
                    {`{{${variable}}}`}
                  </Button>
                ))}
              </div>
              {formData.variables.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Selected variables:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {formData.variables.map((variable) => (
                      <Badge key={variable} variant="secondary">
                        {`{{${variable}}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Template is active
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                <Save className="h-4 w-4 mr-2" />
                {template ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Category Modal */}
      <SingleSelectFilterModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSelect={(value) => handleInputChange('category', value)}
        selectedValue={formData.category}
        options={categoryOptions}
        title="Select Category"
        placeholder="Select category"
        searchPlaceholder="Search categories..."
        required={true}
      />
    </div>
  );
};

// Template Preview Component
const TemplatePreview = ({ template, onClose }) => {
  const [sampleData] = useState({
    ticket_number: 'TKT-001',
    customer_name: 'John Doe',
    agent_name: 'Jane Smith',
    department: 'Support',
    priority: 'High',
    status: 'Open',
    created_at: new Date().toLocaleDateString(),
    subject: 'Test Email Subject',
    description: 'This is a test ticket description.',
    resolution: 'Issue has been resolved.',
    company_name: 'Your Company'
  });

  const processContent = (content) => {
    let processedContent = content;
    Object.keys(sampleData).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processedContent = processedContent.replace(regex, sampleData[key]);
    });
    return processedContent;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Template Preview
            </h2>
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject
              </label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {processContent(template.subject)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content
              </label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg whitespace-pre-wrap">
                {processContent(template.content)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sample Data Used
              </label>
              <div className="p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <pre className="text-sm text-blue-800 dark:text-blue-200">
                  {JSON.stringify(sampleData, null, 2)}
                </pre>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t mt-6">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplates;
